import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { useCookies } from 'next-client-cookies';
import { Category } from 'shared/types/product';
import { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import Breadcrumb from 'components/commercetools-ui/molecules/breadcrumb';
import Wrapper from 'components/HOC/wrapper';
import { mapCategotry } from 'helpers/entity-mappers/map-category';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { resolvedLocalizedObject } from 'helpers/hooks/useResolvedLocalizedObject';
import { desktop } from 'helpers/utils/screensizes';
import { cleanString } from 'helpers/utils/util';
import { useFeatures } from 'providers/feature-flag';
import { Category as CategoryFromEntity } from 'types/entity/category';
import { Product } from 'types/entity/product';
import { Reference } from 'types/reference';
import { TieredPriceData } from 'frontastic/tastics/products/details';
import { getHeadingStyles } from '../../utils';
import { findCatPath } from '../product-details/helpers';
import DesktopFacets from './components/desktop-facets';
import List from './components/list';
import MobileFacets from './components/mobile-facets';
import SearchHeader from './components/search-header';
import SubCategoriesBanner from './components/sub-category-banner';
import { useProductList } from './context';
import { SortConfiguration, SubCategoriesDataProps, SubCategoryBannerProps, TextConfiguration } from './types';

export interface ProductListProps {
  products: Product[];
  textConfiguration?: TextConfiguration[];
  categories: Category[];
  fontConfig?: Record<string, any>;
  sortConfiguration?: SortConfiguration[];
  subCatConfig?: SubCategoryBannerProps[];
  subCategories?: SubCategoriesDataProps;
  textConfig?: Record<string, string>;
  tieredPrice?: TieredPriceData[];
  calendarOptions?: any;
  viewCategoryFacets?: any;
  viewDynamicFacets?: any;
  primaryLogoLight?: ImageProps;
  primaryLogoDark?: ImageProps;
  primaryLogoLink?: Reference;
  primaryLogoLinkMobile?: Reference;
  primaryLogoMobileDark?: ImageProps;
  primaryLogoMobileLight?: ImageProps;
  tagsToShow?: string;
  facetLoadMoreText?: string;
  facetSeeLessText?: string;
  pageName?: string | Record<string, string> | [];
  pageHeading?: string;
  isDealsPage?: boolean;
}

const ProductList: React.FC<ProductListProps> = ({
  products,
  textConfiguration,
  categories: allCategories,
  fontConfig,
  sortConfiguration,
  subCatConfig,
  subCategories,
  textConfig,
  tieredPrice,
  calendarOptions,
  viewCategoryFacets,
  viewDynamicFacets,
  primaryLogoLight,
  primaryLogoDark,
  primaryLogoLink,
  primaryLogoLinkMobile,
  primaryLogoMobileDark,
  primaryLogoMobileLight,
  tagsToShow,
  facetLoadMoreText,
  facetSeeLessText,
  pageName,
  pageHeading,
  isDealsPage,
}) => {
  const { slug, searchQuery, categories } = useProductList();
  const { enabledIndividualPageForCategories } = useFeatures();

  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const [openFacets, setOpenFacets] = useState<boolean>(false);
  const { paginationText, tileCTA, noResultsPlP, sortbyLabel, filterLabel, pickUpToday } = textConfiguration?.[0] ?? {};
  const [isDesktopSize] = useMediaQuery(desktop);

  const homeBreadCrumb = {
    name: textConfiguration?.[0]?.breadCrumbsHome || 'Home',
    categoryId: '_home',
    _url: '/',
  } as CategoryFromEntity;

  const currentCategory = useMemo(() => categories?.find((category) => category.slug === slug), [categories, slug]);
  const searchParams = useSearchParams();

  const plpBreadcrumbs = findCatPath(
    allCategories.map((category) => mapCategotry(category, { locale })),
    currentCategory?.parentId,
  );
  const isPLP = Boolean(subCatConfig?.[0]?.isPLP);
  const overallBreadCrumbs = [{ ...homeBreadCrumb }, ...(plpBreadcrumbs ?? [])];

  const params = useParams();

  const isPreviewSRP =
    Boolean(params?.previewId) &&
    Boolean(pageName) &&
    typeof pageName == 'string' &&
    !Array.isArray(pageName) &&
    resolvedLocalizedObject(pageName, locale) === 'SRP';

  /*
   * An empty string is still considered a search results page.
   */
  const isSearchResultsPage = searchQuery !== undefined && searchQuery !== null;

  const shouldRenderSearchPage = isSearchResultsPage || isPreviewSRP;

  /*
   * Old logic:
   * When the feature flag is disabled, category breadcrumb and
   * SubCategoriesBanner are rendered.
   *
   * New logic:
   * When the feature flag is enabled, category breadcrumb and
   * configurable heading are rendered only when isPLP is true.
   */
  const shouldRenderCategoryBreadcrumb = !enabledIndividualPageForCategories || isPLP;

  const breadcrumbRef = useRef<HTMLAnchorElement>(null);
  const initialFocusDone = useRef(false);

  useEffect(() => {
    if (isDesktopSize && openFacets && !initialFocusDone.current) {
      initialFocusDone.current = true;
    }
  }, [isDesktopSize, openFacets]);

  const hasChannelKeys = searchParams?.has('channelKeys');

  const renderHeaderContent = () => {
    if (shouldRenderSearchPage) {
      return (
        <Wrapper containerWidth clearDefaultStyles>
          <SearchHeader
            query={searchQuery || (isPreviewSRP ? 'mug' : '')}
            productLength={products.length}
            textConfiguration={textConfiguration}
          />
        </Wrapper>
      );
    }

    /*
     * New logic:
     * Use the new configurable category heading only when:
     * 1. The feature flag is enabled.
     * 2. The page is configured as a PLP.
     */
    if (enabledIndividualPageForCategories) {
      if (!isPLP) {
        return null;
      }

      const primaryHeadingConfig = subCatConfig?.[0]?.containerConfig?.[0]?.primaryHeading?.[0];

      return (
        <div className="container py-0 sm:px-0">
          <h3
            className="mb-25 text-center md:mb-26"
            data-ffid={`lbl-category-heading${currentCategory?.name ? '-' + cleanString(currentCategory.name) : ''}`}
            style={getHeadingStyles(
              [
                {
                  desktopHeadingFontSize: primaryHeadingConfig?.fontSizeDesktop,
                  mobileHeadingFontSize: primaryHeadingConfig?.fontSizeMobile,
                  desktopHeadingFontColor: primaryHeadingConfig?.textColor,
                  mobileHeadingFontColor: primaryHeadingConfig?.textColor,
                  desktopHeadingFontWeight: primaryHeadingConfig?.primaryHeadingTextStyle,
                  mobileHeadingFontWeight: primaryHeadingConfig?.primaryHeadingTextStyle,
                },
              ],
              isDesktopSize,
            )}
          >
            {isDealsPage ? pageHeading : currentCategory?.name || ''}
          </h3>
        </div>
      );
    }

    /*
     * Old logic:
     * Render SubCategoriesBanner when the feature flag is disabled.
     */
    return (
      <SubCategoriesBanner
        subCategories={
          subCategories ?? {
            items: [],
            count: 0,
            total: 0,
          }
        }
        subCatConfig={subCatConfig ?? []}
        currentCategory={(isDealsPage ? pageHeading : currentCategory?.name) ?? ''}
      />
    );
  };

  const renderBreadcrumb = () => {
    if (shouldRenderSearchPage) {
      return (
        <Breadcrumb
          className="mx-auto max-w-screen-xl pb-25 transition-all duration-300 md:pl-24 lg:block lg:pl-24"
          backCategory={homeBreadCrumb}
          listClassName="w-fit"
          ref={breadcrumbRef}
        >
          <Link link="/" className="truncate text-14" data-ffid="lnk-breadcrumb-link-0">
            <p title="Home" data-ffid="lbl-breadcrumb-category-title-0" className="text-14">
              {homeBreadCrumb.name}
            </p>
          </Link>

          <p
            className="max-w-170 cursor-default truncate text-14 font-bold lg:max-w-250"
            title="Search Results"
            data-ffid="lbl-breadcrumb-product-title"
          >
            {isDesktopSize && textConfiguration?.[0]?.breadCrumbsSearch}
          </p>
        </Breadcrumb>
      );
    }

    /*
     * Old logic:
     * Always render category breadcrumb when the flag is disabled.
     *
     * New logic:
     * Render category breadcrumb only when isPLP is true.
     */
    if (!shouldRenderCategoryBreadcrumb) {
      return null;
    }

    return (
      <Breadcrumb
        className="mx-auto max-w-screen-xl transition-all duration-300 md:pl-24 lg:mb-24 lg:block lg:pl-24"
        backCategory={overallBreadCrumbs.at(-1)}
        listClassName="w-fit"
        ref={breadcrumbRef}
      >
        {overallBreadCrumbs.map((item, index) => (
          <Link
            key={item?.categoryId}
            link={item?._url}
            className="max-w-170 truncate text-14 lg:max-w-250"
            data-ffid={`lnk-breadcrumb-link-${index}`}
          >
            <p title={item?.name} data-ffid={`lbl-breadcrumb-category-title-${index}`} className="text-14">
              {overallBreadCrumbs.length > 2 && index !== overallBreadCrumbs.length - 1 ? '...' : (item?.name ?? '...')}
            </p>
          </Link>
        ))}

        <p
          className="max-w-170 cursor-default truncate text-14 font-bold lg:max-w-250"
          title={currentCategory?.name}
          data-ffid="lbl-breadcrumb-product-title"
        >
          {isDesktopSize && (isDealsPage ? pageHeading : currentCategory?.name)}
        </p>
      </Breadcrumb>
    );
  };

  return (
    <div className="min-h-full bg-white pb-48 dark:bg-gmsb-header-gray">
      <div className="mx-20 sm:mx-24 md:mx-60 lg:mx-76 xl:mx-0">{renderBreadcrumb()}</div>

      <div className="mx-20 sm:mx-24 md:mx-60 lg:mx-76 xl:mx-0">{renderHeaderContent()}</div>

      {products.length > 0 || Boolean(searchParams.get('wishListIds[0]')) ? (
        <Wrapper clearDefaultStyles className="relative dark:bg-gmsb-header-gray">
          {isDesktopSize ? (
            <DesktopFacets
              sortbyLabel={sortbyLabel}
              pickUpToday={pickUpToday}
              filterLabel={filterLabel}
              sortConfiguration={sortConfiguration}
              setOpenFacets={setOpenFacets}
              openFacets={openFacets}
            />
          ) : (
            <MobileFacets
              sortbyLabel={sortbyLabel}
              pickUpToday={pickUpToday}
              filterLabel={filterLabel}
              sortConfiguration={sortConfiguration}
              setOpenFacets={setOpenFacets}
              openFacets={openFacets}
            />
          )}

          <List
            tagsToShow={tagsToShow}
            facetLoadMoreText={facetLoadMoreText}
            facetSeeLessText={facetSeeLessText}
            products={products}
            sortConfiguration={sortConfiguration}
            tileCTA={tileCTA}
            paginationText={paginationText}
            categories={allCategories}
            config={fontConfig}
            textConfig={textConfig}
            tieredPrice={tieredPrice}
            calendarOptions={calendarOptions}
            setOpenFacets={setOpenFacets}
            openFacets={openFacets}
            viewCategoryFacets={viewCategoryFacets}
            viewDynamicFacets={viewDynamicFacets}
            primaryLogoLight={primaryLogoLight}
            primaryLogoDark={primaryLogoDark}
            primaryLogoLink={primaryLogoLink}
            primaryLogoLinkMobile={primaryLogoLinkMobile}
            primaryLogoMobileDark={primaryLogoMobileDark}
            primaryLogoMobileLight={primaryLogoMobileLight}
            noResultsPlP={noResultsPlP}
            isToggleP2r={hasChannelKeys}
          />
        </Wrapper>
      ) : (
        !searchQuery && (
          <h3
            data-ffid="lbl-no-results-found-text"
            className="mt-4 p-20 pb-0 text-center text-18 text-gray-500 lg:text-28"
          >
            {noResultsPlP}
          </h3>
        )
      )}
    </div>
  );
};

export default ProductList;
